'use client';

import { c as _c } from "react/compiler-runtime";
import React, { FC, useState } from 'react';
import { useIsClaiStaff } from '@lib/auth/hooks/use-is-clai-staff';
import { cn } from 'cn';
import { AlertCircle, ChevronDown, Cpu, Loader2, type LucideIcon } from 'lucide-react';
import { Button } from './button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './collapsible';
type ErrorIndicatorVariant = 'compact' | 'inline' | 'panel' | 'centered' | 'centered-dark';
export interface ErrorIndicatorProps {
  message?: string;
  /** Friendly one-liner shown as the heading in the `centered` / `centered-dark` variants. Defaults to `message` when omitted. */
  title?: string;
  retryLabel?: string;
  onRetry?: () => void;
  retryLoading?: boolean;
  retryLoadingMessage?: string;
  variant?: ErrorIndicatorVariant;
  /** Custom icon rendered in the bubble. Defaults to AlertCircle. Only used in `centered` and `centered-dark` variants. */
  icon?: LucideIcon;
  /**
   * Raw technical detail (e.g. a caught error message) shown in an expandable
   * section. Only rendered for @goclai.com staff.
   */
  technicalDetail?: string;
  className?: string;
}
const DEFAULT_ERROR_MESSAGE = "We couldn't load this. We've been automatically notified — please try again in a moment.";
const DEFAULT_LOADING_MESSAGE = 'Trying again...';
const DEFAULT_CENTERED_TITLE = 'Something went wrong';
const variantClassNames: Record<ErrorIndicatorVariant, string> = {
  compact: 'flex flex-col items-center gap-3 text-center',
  inline: 'flex min-h-14 w-full flex-col gap-3 rounded-lg border border-destructive/20 bg-destructive/5 p-3 sm:flex-row sm:items-center sm:justify-between',
  panel: 'flex min-h-36 w-full flex-col items-center justify-center gap-4 rounded-lg border border-dashed border-destructive/25 bg-destructive/5 px-6 py-8 text-center',
  centered: 'flex w-full flex-col items-center justify-center gap-4 rounded-lg border border-border bg-card px-8 py-10 text-center',
  'centered-dark': 'flex w-full flex-col items-center justify-center gap-5 rounded-2xl border border-white/10 bg-white/[0.04] px-5 py-8 text-center'
};
const iconClassNames: Record<ErrorIndicatorVariant, string> = {
  compact: 'h-8 w-8',
  inline: 'mt-0.5 h-5 w-5 sm:mt-0',
  panel: 'h-9 w-9',
  centered: 'h-6 w-6',
  'centered-dark': 'h-7 w-7'
};
function TechnicalDetails(t0) {
  const $ = _c(10);
  const {
    detail
  } = t0;
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] !== open) {
    t1 = cn("h-3.5 w-3.5 shrink-0 transition-transform duration-200", {
      "rotate-180": open
    });
    $[0] = open;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <CollapsibleTrigger asChild={true} data-sentry-element="CollapsibleTrigger" data-sentry-source-file="error-indicator.tsx"><button className="border-border hover:bg-muted text-muted-foreground flex w-full items-center gap-2 rounded-lg border px-3 py-2.5 text-xs transition-colors"><ChevronDown aria-hidden="true" className={t1} data-sentry-element="ChevronDown" data-sentry-source-file="error-indicator.tsx" />Technical details</button></CollapsibleTrigger>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== detail) {
    t3 = <CollapsibleContent data-sentry-element="CollapsibleContent" data-sentry-source-file="error-indicator.tsx"><pre className="border-border bg-muted text-muted-foreground mt-2 w-full rounded-md border px-3 py-2.5 text-left font-mono text-xs break-all whitespace-pre-wrap">{detail}</pre></CollapsibleContent>;
    $[4] = detail;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== open || $[7] !== t2 || $[8] !== t3) {
    t4 = <Collapsible open={open} onOpenChange={setOpen} className="w-full" data-sentry-element="Collapsible" data-sentry-component="TechnicalDetails" data-sentry-source-file="error-indicator.tsx">{t2}{t3}</Collapsible>;
    $[6] = open;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function TechnicalDetailsDark(t0) {
  const $ = _c(13);
  const {
    detail
  } = t0;
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] !== open) {
    t1 = cn("h-4 w-4 transition-transform duration-200", {
      "rotate-180": open
    });
    $[0] = open;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <span className="flex items-center gap-2 text-sm text-white/60"><ChevronDown aria-hidden="true" className={t1} data-sentry-element="ChevronDown" data-sentry-source-file="error-indicator.tsx" />Technical details</span>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="flex items-center gap-1 rounded-full bg-blue-500/20 px-2 py-0.5 text-[11px] text-blue-400"><Cpu aria-hidden="true" className="h-3 w-3" data-sentry-element="Cpu" data-sentry-source-file="error-indicator.tsx" />internal</span>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2) {
    t4 = <CollapsibleTrigger asChild={true} data-sentry-element="CollapsibleTrigger" data-sentry-source-file="error-indicator.tsx"><button className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 transition-colors hover:bg-white/[0.08]">{t2}{t3}</button></CollapsibleTrigger>;
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== detail) {
    t5 = <CollapsibleContent data-sentry-element="CollapsibleContent" data-sentry-source-file="error-indicator.tsx"><pre className="mt-2 w-full rounded-xl border border-white/10 bg-black/30 px-3 py-3 text-left font-mono text-xs break-all whitespace-pre-wrap text-white/50">{detail}</pre></CollapsibleContent>;
    $[7] = detail;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== open || $[10] !== t4 || $[11] !== t5) {
    t6 = <Collapsible open={open} onOpenChange={setOpen} className="w-full" data-sentry-element="Collapsible" data-sentry-component="TechnicalDetailsDark" data-sentry-source-file="error-indicator.tsx">{t4}{t5}</Collapsible>;
    $[9] = open;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
}
export const ErrorIndicator: FC<ErrorIndicatorProps> = ({
  message = DEFAULT_ERROR_MESSAGE,
  title,
  retryLabel = 'Try Again',
  onRetry,
  retryLoading = false,
  retryLoadingMessage = DEFAULT_LOADING_MESSAGE,
  variant = 'inline',
  icon,
  technicalDetail,
  className
}) => {
  const isStaff = useIsClaiStaff();
  const displayedMessage = retryLoading ? retryLoadingMessage : message;
  const ErrorIcon = icon ?? AlertCircle;
  const StatusIcon = retryLoading ? Loader2 : ErrorIcon;
  const handleRetry = () => {
    if (!onRetry || retryLoading) return;
    onRetry();
  };
  if (variant === 'centered' || variant === 'centered-dark') {
    const isDark = variant === 'centered-dark';
    const displayedTitle = retryLoading ? retryLoadingMessage : title ?? DEFAULT_CENTERED_TITLE;
    return <div aria-atomic="true" aria-busy={retryLoading} aria-live="polite" className={cn(variantClassNames[variant], className)} role={retryLoading ? 'status' : 'alert'}>
        <div className={cn('flex shrink-0 items-center justify-center rounded-full', isDark ? 'h-14 w-14' : 'h-11 w-11', {
        [isDark ? 'bg-white/10' : 'bg-muted']: retryLoading,
        'bg-destructive/20': !retryLoading
      })}>
          <StatusIcon aria-hidden="true" className={cn(iconClassNames[variant], {
          'animate-spin': retryLoading,
          [isDark ? 'text-white/40' : 'text-muted-foreground']: retryLoading,
          'text-destructive': !retryLoading
        })} data-testid="alert-circle-icon" />
        </div>

        <div className="flex flex-col items-center gap-1.5">
          <p className={cn(isDark ? 'text-base font-bold' : 'text-sm font-medium', {
          'text-white': isDark && !retryLoading,
          'text-foreground': !isDark && !retryLoading,
          [isDark ? 'text-white/50' : 'text-muted-foreground']: retryLoading
        })}>
            {displayedTitle}
          </p>
          {!retryLoading && <p className={cn('max-w-xs text-sm', {
          'text-white/60': isDark,
          'text-muted-foreground': !isDark
        })}>
              {displayedMessage}
            </p>}
        </div>

        {onRetry && !retryLoading && <Button type="button" onClick={handleRetry} variant={isDark ? 'outline' : 'secondary'} size="sm" className={cn({
        'border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white': isDark
      })}>
            {retryLabel}
          </Button>}

        {isStaff && technicalDetail && !retryLoading && (isDark ? <TechnicalDetailsDark detail={technicalDetail} /> : <TechnicalDetails detail={technicalDetail} />)}
      </div>;
  }
  return <div aria-atomic="true" aria-busy={retryLoading} aria-live="polite" className={cn(variantClassNames[variant], className)} role={retryLoading ? 'status' : 'alert'} data-sentry-component="ErrorIndicator" data-sentry-source-file="error-indicator.tsx">
      <div className={cn('flex min-w-0 items-center gap-3', variant === 'compact' || variant === 'panel' ? 'flex-col' : 'w-full sm:w-auto')}>
        <StatusIcon aria-hidden="true" className={cn('shrink-0', iconClassNames[variant], {
        'text-muted-foreground animate-spin': retryLoading,
        'text-destructive': !retryLoading
      })} data-testid="alert-circle-icon" data-sentry-element="StatusIcon" data-sentry-source-file="error-indicator.tsx" />
        <div className={cn('min-w-0', {
        'text-left': variant === 'inline',
        'text-center': variant !== 'inline'
      })}>
          <p className={cn('text-sm font-semibold', {
          'text-muted-foreground': retryLoading,
          'text-destructive': !retryLoading
        })}>
            {displayedMessage}
          </p>
        </div>
      </div>
      {onRetry && !retryLoading && <Button type="button" aria-label={retryLabel} onClick={handleRetry} variant="outline" size="sm">
          {retryLabel}
        </Button>}
    </div>;
};
export default ErrorIndicator;